import type { ReactNode } from 'react'
import { tokens } from '@/theme'
import { MOBILE_MAX } from './responsive'

interface Props {
  /** Small uppercase label above the headline. Chat omits it. */
  eyebrow?: string
  /**
   * Leading half of the headline, rendered at 55% per the brand's split
   * headline. Omit for a single-tone headline.
   */
  headlineMuted?: string
  headline: string
  lede?: string
  /** The ambient scene on the right. */
  aside?: ReactNode
  /** Width of the aside column in px. */
  asideWidth?: number
  /** Vertical padding — the design runs 34px on Chat, 44px elsewhere. */
  padY?: number
  /** Headline size. 30px on Chat, 42px on the list pages. */
  headlineSize?: number
}

/**
 * The dark band each inner page opens with, sitting flush under the header's
 * ink panel: gold rule, eyebrow, split headline, lede, and an ambient scene.
 *
 * Home builds its own, taller hero — it carries the ask box and the stats
 * band, which no other page has.
 */
export function PageHero({
  eyebrow,
  headlineMuted,
  headline,
  lede,
  aside,
  asideWidth = 320,
  padY = 44,
  headlineSize = 42,
}: Props) {
  return (
    <section style={{ position: 'relative', background: tokens.inkPanel, overflow: 'hidden' }}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 85% 40%, rgba(153,130,66,0.12) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="orion-page-hero orion-pad"
        style={{
          position: 'relative',
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `${padY}px ${tokens.containerPadX}px`,
          display: 'grid',
          gridTemplateColumns: aside ? `minmax(0, 1fr) ${asideWidth}px` : 'minmax(0, 1fr)',
          gap: 32,
          alignItems: 'center',
        }}
      >
        <div>
          <div
            style={{
              width: 40,
              height: 2,
              background: tokens.gold,
              marginBottom: 16,
              transformOrigin: 'left',
              animation: `barGrow ${tokens.durSlow} ${tokens.easeStandard} both`,
            }}
          />
          {eyebrow && (
            <div
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 11,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: tokens.inkPanelText,
                marginBottom: 12,
              }}
            >
              {eyebrow}
            </div>
          )}
          <h1
            className="orion-page-hero-headline"
            style={{
              fontFamily: tokens.fontDisplay,
              fontSize: headlineSize,
              fontWeight: tokens.weightDemi,
              letterSpacing: '-0.015em',
              lineHeight: 1.05,
              color: tokens.paper,
              margin: 0,
            }}
          >
            {headlineMuted && <span style={{ opacity: 0.55 }}>{headlineMuted} </span>}
            {headline}
          </h1>
          {lede && (
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                color: tokens.inkPanelText,
                margin: '14px 0 0',
                maxWidth: 560,
              }}
            >
              {lede}
            </p>
          )}
        </div>

        {aside && <div className="orion-page-hero-aside">{aside}</div>}
      </div>
    </section>
  )
}

/**
 * Responsive rules for the hero. Inline styles can't express media queries
 * and the app carries no stylesheet, so — as in App.tsx and HomeView — the
 * few that are needed live in a scoped style block. Mount once per page.
 */
export const pageHeroStyles = `
@media (max-width: 900px) {
  .orion-page-hero { grid-template-columns: minmax(0, 1fr) !important; }
  .orion-page-hero-aside { display: none; }
}
/* On a phone the band is pure overhead above the content the page is for:
   it keeps the headline and loses the room around it. */
@media (max-width: ${MOBILE_MAX}px) {
  .orion-page-hero { padding-top: 26px !important; padding-bottom: 26px !important; }
  .orion-page-hero-headline { font-size: 28px !important; }
}
`
